import Typewriter from "typewriter-effect";
import { NavLink } from "react-router-dom";
import { useProfile } from "../../components/portfolio/context/ProfileContext";

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const DEFAULT_TYPEWRITER_HTML = [
  '<span style="color: var(--primary-color);">Full Stack Engineer</span>',
  '<span style="color: var(--primary-color);">React and Node.js Developer</span>',
  '<span style="color: var(--primary-color);">AWS Cloud Specialist</span>',
  '<span style="color: var(--primary-color);">Building Scalable Web Apps</span>',
];

function splitGreeting(greeting) {
  const trimmed = greeting.trim();
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 3) {
    const name = `${parts[parts.length - 2]} ${parts[parts.length - 1]}`;
    const lead = parts.slice(0, -2).join(" ");
    return { lead, name };
  }
  return { lead: "", name: trimmed };
}

function StatIcon({ type }) {
  const common = { width: 22, height: 22, viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg" };
  switch (type) {
    case "folder":
      return (
        <svg {...common} aria-hidden="true">
          <path
            d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "code":
      return (
        <svg {...common} aria-hidden="true">
          <path d="M8 7L3 12l5 5M16 7l5 5-5 5M13.5 4l-3 16" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
        </svg>
      );
    case "star":
      return (
        <svg {...common} aria-hidden="true">
          <path
            d="M12 3l2.4 7.4h7.6l-6 4.6 2.3 7L12 17.8 5.7 22l2.3-7-6-4.6h7.6L12 3z"
            fill="currentColor"
            stroke="currentColor"
            strokeWidth="0.5"
          />
        </svg>
      );
    case "rocket":
    default:
      return (
        <svg {...common} aria-hidden="true">
          <path
            d="M12 3c1.5 3 2 5.5 2 8v3l2 1-2 6-2-4-2 4-2-6 2-1v-3c0-2.5.5-5 2-8z"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinejoin="round"
            fill="none"
          />
          <path d="M12 11v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
  }
}

function Intro() {
  const { intro } = useProfile();

  const greeting = intro?.greeting?.trim() || "Hi, I am Sunil Rawat";
  const bio =
    intro?.bio?.trim() ||
    "I am skilled full stack developer with decent experience.";

  const { lead, name } = splitGreeting(greeting);

  const typewriterStrings =
    intro?.rotatingTitles?.length > 0
      ? intro.rotatingTitles.map(
          (t) => `<span style="color: var(--primary-color);">${escapeHtml(t)}</span>`
        )
      : DEFAULT_TYPEWRITER_HTML;

  const heroStats = Array.isArray(intro?.heroStats) && intro.heroStats.length ? intro.heroStats : [];

  return (
    <section id="intro-section" className="intro-hero">
      <div className="intro-hero-decor" aria-hidden="true" />
      <div className="intro-hero-inner">
        <h1 className="intro-heading">
          {lead ? <span className="intro-lead">{lead}</span> : null}
          <span className="intro-name-gradient">{name}</span>
        </h1>

        <div className="intro-typewriter-line">
          <Typewriter
            options={{
              strings: typewriterStrings,
              autoStart: true,
              loop: true,
              delay: 77,
              deleteSpeed: 40,
              pauseFor: 2000,
            }}
            wrapperClassName="intro-typewriter"
            cursorClassName="intro-typewriter-cursor"
          />
        </div>

        <div className="intro-accent-bar" aria-hidden="true" />

        <p className="intro-bio">{bio}</p>

        <div className="intro-actions">
          <a
            className="intro-btn intro-btn-linkedin"
            href="https://www.linkedin.com/in/sunil-rawat-0059861ab/"
            target="_blank"
            rel="noreferrer"
          >
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" className="intro-btn-brand-icon">
              <path
                fill="currentColor"
                d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"
              />
            </svg>
            My LinkedIn Profile
          </a>
          <a
            className="intro-btn intro-btn-github"
            href="https://github.com/sunilrawatofficial"
            target="_blank"
            rel="noreferrer"
          >
            <svg viewBox="0 0 25 25" width="22" height="22" aria-hidden="true" className="intro-btn-brand-icon">
              <path
                fill="currentColor"
                d="M12.5103 0C5.59245 0 0 5.72914 0 12.8169C0 18.4825 3.58327 23.2783 8.55422 24.9757C9.17572 25.1033 9.40337 24.6999 9.40337 24.3606C9.40337 24.0634 9.38289 23.045 9.38289 21.9838C5.90281 22.7478 5.17812 20.4559 5.17812 20.4559C4.61885 18.9705 3.79018 18.5887 3.79018 18.5887C2.65116 17.8036 3.87315 17.8036 3.87315 17.8036C5.13663 17.8885 5.79961 19.1192 5.79961 19.1192C6.9179 21.0713 8.7199 20.5197 9.44486 20.1801C9.54831 19.3525 9.87993 18.7796 10.232 18.4614C7.45642 18.1642 4.53613 17.0609 4.53613 12.1377C4.53613 10.7372 5.03292 9.59137 5.8201 8.70022C5.6959 8.382 5.26083 7.06612 5.94455 5.30493C5.94455 5.30493 7.00087 4.96534 9.38263 6.62055C10.4023 6.33999 11.454 6.19727 12.5103 6.19607C13.5667 6.19607 14.6435 6.34477 15.6378 6.62055C18.0198 4.96534 19.0761 5.30493 19.0761 5.30493C19.7599 7.06612 19.3245 8.382 19.2003 8.70022C20.0083 9.59137 20.4846 10.7372 20.4846 12.1377C20.4846 17.0609 17.5643 18.1429 14.7679 18.4614C15.2237 18.8645 15.6171 19.6283 15.6171 20.8379C15.6171 22.5567 15.5966 23.9361 15.5966 24.3603C15.5966 24.6999 15.8245 25.1033 16.4457 24.9759C21.4167 23.278 24.9999 18.4825 24.9999 12.8169C25.0204 5.72914 19.4075 0 12.5103 0Z"
              />
            </svg>
            My Github Profile
          </a>
          <NavLink className="intro-btn intro-btn-secondary" to="/contact">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              width="20"
              height="20"
              aria-hidden="true"
            >
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="M3 7l9 6 9-6" />
            </svg>
            Contact Me
          </NavLink>
        </div>

        {heroStats.length > 0 ? (
          <ul className="intro-stats">
            {heroStats.map((item) => (
              <li key={`${item.label}-${item.value}`} className="intro-stat-card">
                <span className={`intro-stat-icon intro-stat-icon--${item.icon || "rocket"}`}>
                  <StatIcon type={item.icon} />
                </span>
                <span className="intro-stat-text">
                  <span className="intro-stat-value">{item.value}</span>
                  <span className="intro-stat-label">{item.label}</span>
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
}

export default Intro;
